import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/SiteShell";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { getAboutUsContent } from "@/lib/aboutUsContent.functions";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import { cachedSSR } from "@/lib/ssrCache";
import heroFallback from "@/assets/story-image.jpg?format=avif&quality=80";
import storyFallback from "@/assets/home-story-perfume-frame.jpg?format=avif&quality=80";
import storyFallbackWebp from "@/assets/home-story-perfume-frame.jpg?format=webp&quality=80";
import expFallback from "@/assets/gift-collection.jpg?format=avif&quality=80";
import expFallbackWebp from "@/assets/gift-collection.jpg?format=webp&quality=80";
import uaeFallback from "@/assets/editorial-2.jpg?format=avif&quality=80";
import uaeFallbackWebp from "@/assets/editorial-2.jpg?format=webp&quality=80";
import finalFallback from "@/assets/editorial-1.jpg?format=avif&quality=80";
import finalFallbackWebp from "@/assets/editorial-1.jpg?format=webp&quality=80";
import { softCtaClass } from "@/components/ui/soft-cta";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/about")({
  headers: publicCacheHeaders,
  // ⚡ TTFB: القراءة مخزَّنة على السيرفر — أوّل بايت لا ينتظر قاعدة البيانات.
  loader: async () =>
    cachedSSR(
      "about-content",
      async () => {
        const { images, texts } = await getAboutUsContent();
        return { images, texts };
      },
      { images: {} as Awaited<ReturnType<typeof getAboutUsContent>>["images"], texts: {} as Awaited<ReturnType<typeof getAboutUsContent>>["texts"] },
      // محتوى الصفحة نصّها الأساسيّ — ننتظره فعلاً بدل عرض نصّ افتراضيّ، ونحدّثه كلّ دقيقتين.
      { ttlMs: 120_000, timeoutMs: 2_500 },
    ),
  head: () => ({
    meta: [
      { title: DICTS.en["aboutus.meta.title"] },
      { name: "description", content: DICTS.en["aboutus.meta.description"] },
      { property: "og:title", content: DICTS.en["aboutus.meta.title"] },
      { property: "og:description", content: DICTS.en["aboutus.meta.description"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/about` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/about` },
      { rel: "alternate", hrefLang: "en", href: `${PUBLIC_SITE_ORIGIN}/about` },
      { rel: "alternate", hrefLang: "ar", href: `${PUBLIC_SITE_ORIGIN}/about` },
      { rel: "alternate", hrefLang: "x-default", href: `${PUBLIC_SITE_ORIGIN}/about` },
      { rel: "preload", as: "image", href: heroFallback, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "LECMO",
          url: PUBLIC_SITE_ORIGIN,
          foundingDate: "2017",
          foundingLocation: {
            "@type": "Place",
            address: { "@type": "PostalAddress", addressCountry: "AE" },
          },
          description: DICTS.en["aboutus.meta.description"],
        }),
      },
    ],
  }),
  component: AboutPage,
});

const SECTION = "mx-auto w-full max-w-6xl px-4 sm:px-6";

function AboutPage() {
  const { t, locale } = useLocale();
  const { images, texts } = Route.useLoaderData();
  const isAr = locale === "ar";

  // نصّ الصفحة: تجاوز الأدمن أولاً، ثمّ القاموس.
  const tx = React.useCallback(
    (key: string) => {
      const ov = texts?.[key]?.[isAr ? "ar" : "en"];
      return (ov && ov.trim()) || t(key as never);
    },
    [texts, isAr, t],
  );

  const heroImage = images.hero_desktop || images.hero_mobile || heroFallback;

  return (
    <SiteShell>
      <div className="overflow-x-hidden bg-background">
        {/* ── HERO الموحَّد (نفس ارتفاع هيرو الهوم) ── */}
        <PageHero
          pageKey="about"
          title={tx("aboutus.hero.title")}
          subtitle={tx("aboutus.hero.body")}
          image={heroImage}
          alt={tx("aboutus.hero.image_alt")}
          cta={
            <Link to="/shop" className={softCtaClass}>
              {tx("aboutus.hero.cta")}
            </Link>
          }
        />


        {/* ── OUR STORY ── */}
        <section className={`${SECTION} border-t border-foreground/15 py-14 md:py-20`}>
          <div className="grid items-center gap-10 md:grid-cols-2 md:gap-14">
            <Reveal className="order-1 md:order-none">
              <picture>
                {!images.story && <source srcSet={storyFallbackWebp} type="image/webp" />}
                {!images.story && <source srcSet={storyFallback} type="image/avif" />}
                <img
                  src={images.story || storyFallback}
                  alt={tx("aboutus.story.image_alt")}
                  width={1200}
                  height={1500}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover"
                />
              </picture>
            </Reveal>
            <Reveal delayMs={80} className="order-2 md:order-none">
              <p className="t-eyebrow-muted">{tx("aboutus.story.eyebrow")}</p>
              <h2 className="t-h2 mt-4 text-foreground">{tx("aboutus.story.title")}</h2>
              <p className="t-body mt-5 text-muted-foreground">{tx("aboutus.story.p1")}</p>
              <p className="t-body mt-4 text-muted-foreground">{tx("aboutus.story.p2")}</p>
            </Reveal>
          </div>
        </section>

        {/* ── BRAND QUOTE ── */}
        <section className="border-y border-foreground/15 bg-background">
          <Reveal className={`${SECTION} py-16 text-center md:py-24`}>
            <p className="t-h2 mx-auto max-w-3xl text-foreground">{tx("aboutus.quote.text")}</p>
            <p className="t-eyebrow-muted mt-8">LECMO</p>
          </Reveal>
        </section>

        {/* ── OUR PHILOSOPHY ── */}
        <section className={`${SECTION} py-14 md:py-20`}>
          <Reveal>
            <p className="t-eyebrow-muted">{tx("aboutus.philosophy.eyebrow")}</p>
          </Reveal>
          <div className="mt-9 grid gap-px border border-foreground/15 bg-foreground/15 md:grid-cols-3">
            {([1, 2, 3] as const).map((i, idx) => (
              <Reveal key={i} delayMs={idx * 90} className="bg-background p-7 md:p-9">
                <span className="t-mono-eyebrow text-muted-foreground">0{i}</span>
                <h3 className="t-h3 mt-4 text-foreground">
                  {tx(`aboutus.philosophy.${i}.title`)}
                </h3>
                <p className="t-body-sm mt-3 text-muted-foreground">
                  {tx(`aboutus.philosophy.${i}.body`)}
                </p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ── THE LECMO EXPERIENCE ── */}
        <section className="border-t border-foreground/15">
          <div className={`${SECTION} py-14 md:py-20`}>
            <div className="grid items-center gap-10 md:grid-cols-2 md:gap-14">
              <Reveal>
                <p className="t-eyebrow-muted">{tx("aboutus.exp.eyebrow")}</p>
                <h2 className="t-h2 mt-4 text-foreground">{tx("aboutus.exp.title")}</h2>
                <p className="t-body mt-5 text-muted-foreground">{tx("aboutus.exp.body")}</p>
                <Link
                  to="/shop"
                  className="t-eyebrow-lg mt-7 inline-flex min-h-[44px] items-center border-b border-foreground/60 pb-1 text-foreground transition-opacity hover:opacity-70"
                >
                  {tx("aboutus.exp.link")}
                </Link>
              </Reveal>
              <Reveal delayMs={80}>
                <picture>
                  {!images.experience && <source srcSet={expFallbackWebp} type="image/webp" />}
                  {!images.experience && <source srcSet={expFallback} type="image/avif" />}
                  <img
                    src={images.experience || expFallback}
                    alt={tx("aboutus.exp.image_alt")}
                    width={1400}
                    height={1000}
                    loading="lazy"
                    className="aspect-[7/5] w-full object-cover"
                  />
                </picture>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ── FROM THE UAE TO THE WORLD ── */}
        <section className="border-t border-foreground/15">
          <div className={`${SECTION} py-14 md:py-20`}>
            <div className="grid items-center gap-10 md:grid-cols-[1.1fr_1fr] md:gap-14">
              <Reveal>
                <picture>
                  {!images.uae && <source srcSet={uaeFallbackWebp} type="image/webp" />}
                  {!images.uae && <source srcSet={uaeFallback} type="image/avif" />}
                  <img
                    src={images.uae || uaeFallback}
                    alt={tx("aboutus.uae.image_alt")}
                    width={1400}
                    height={900}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover"
                  />
                </picture>
              </Reveal>
              <Reveal delayMs={80}>
                <p className="t-eyebrow-muted">{tx("aboutus.uae.eyebrow")}</p>
                <h2 className="t-h2 mt-4 text-foreground">{tx("aboutus.uae.title")}</h2>
                <p className="t-body mt-5 text-muted-foreground">{tx("aboutus.uae.body")}</p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="relative border-t border-foreground/15">
          <picture>
            {!images.cta && <source srcSet={finalFallbackWebp} type="image/webp" />}
            {!images.cta && <source srcSet={finalFallback} type="image/avif" />}
            <img
              src={images.cta || finalFallback}
              alt={tx("aboutus.final.image_alt")}
              width={1800}
              height={1000}
              loading="lazy"
              className="h-[46svh] w-full object-cover md:h-[58svh]"
            />
          </picture>
          <div className="absolute inset-0 bg-foreground/45" />
          <Reveal className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <h2 className="t-h1 max-w-2xl text-background">{tx("aboutus.final.title")}</h2>
            <p className="t-body mt-4 max-w-xl text-background/85">{tx("aboutus.final.body")}</p>
            <Link
              to="/shop"
              className="t-eyebrow-lg mt-8 inline-flex min-h-[48px] items-center justify-center border border-background bg-background px-8 text-foreground transition-colors hover:bg-transparent hover:text-background"
            >
              {tx("aboutus.final.primary")}
            </Link>
          </Reveal>
        </section>
      </div>
    </SiteShell>
  );
}
